import React from 'lib/react';
import {
  EVGrid,
  EVPaper,
  EVCheckbox,
  EVInputLabel,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import useMediaQuery from 'hooks/useMediaQuery';
import Map from 'commonComponents/map';
import { updateFocusedPointIndex } from 'features/order/Order.actions';
import BuildingDetailsListItem from 'commonComponents/building-details-list-item';
import PropTypes from 'prop-types';
import clsx from 'lib/clsx';
import Typography from 'commonComponents/typography';
import { updateChangesIn4Years } from 'features/finalizeOrder/FinalizeOrder.actions';
import { NEW_UNIFIED_SKUS } from 'constants/AppConstants';
import { isEntitledForEmbeddedExplorerSelector } from 'utils/appSelectors.utils';
import { isPropertyDataSelected as checkIsPropertyDataSelected } from '../../../order/Order.utils';
import PropertyDetailsStyles from './PropertyDetailsStyles';
import UpgradeReport from './UpgradeReport.component';

function PropertyDetails({ upgradeReportId }) {
  const {
    mapPoints,
    isComplex,
    propertyType,
    focusedPointIndex,
    bluePrintId,
    bluePrintFileName,
    productSets,
    changesIn4Years,
    bluePrintFileId,
    selectedPrimaryProduct,
  } = useSelector(({
    order, orderAddresses, placeOrder,
  }) => ({
    ...order,
    bluePrintId: placeOrder.bluePrintId,
    bluePrintFileName: placeOrder.bluePrintFileName,
    mapPoints: orderAddresses.mapPoints,
    geocodedAddress: orderAddresses.geocodedAddress,
    focusedPointIndex: orderAddresses.focusedPointIndex,
    productSets: placeOrder.productSets,
    changesIn4Years: placeOrder.changesIn4Years,
    bluePrintFileId: placeOrder.bluePrintFileId,
    selectedPrimaryProduct: order.selectedPrimaryProduct,
  }));

  const classes = PropertyDetailsStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);
  const dispatch = useDispatch();

  const useEE = useSelector(({ order }) => order.useEmbeddedExplorer);

  const isEntitledForEmbeddedExplorer = useSelector(isEntitledForEmbeddedExplorerSelector);

  const handleFocusChange = (index) => dispatch(updateFocusedPointIndex((index === focusedPointIndex) ? null : index));

  const handleRemoveFocus = () => dispatch(updateFocusedPointIndex(null));

  const handleBuildingDetailClick = (index) => (e) => {
    e.stopPropagation();
    handleFocusChange(index);
  };

  const handleChangesIn4YearsCheck = () => dispatch(updateChangesIn4Years());
  const changesInLast4years = () => (
    <EVGrid container alignItems="flex-start" className={classes.checkChangesContainer}>
      <EVCheckbox
        checked={changesIn4Years}
        name="changesIn4Years"
        id="changesIn4Years"
        color="primary"
        className={classes.checkChanges}
        onChange={handleChangesIn4YearsCheck}
      />
      <EVInputLabel htmlFor="changesIn4Years" className={classes.changesIn4YearsLabel}>
        <Typography variant="subtitle-400" fontColor="charcoal-900">
          {t('finalize_order__changes_in_the_last_4_years')}
        </Typography>
        <Typography variant="subtitle-400" fontColor="charcoal-700">
          (
          {t('finalize_order__renovations_etc')}
          )
        </Typography>
      </EVInputLabel>
    </EVGrid>
  );
  const propertyTypeDetails = () => (
    <EVGrid style={{ padding: '0 8px' }}>
      <Typography variant="label-600" fontColor="charcoal-800" className={classes.propertyTypeHeading}>{t('PropertyType')}</Typography>
      <Typography variant="subtitle-400" data-testid="property-type">
        {`${propertyType}${isComplex ? ' Complex' : ''}`}
      </Typography>
    </EVGrid>
  );

  const isNewSKUSelected = productSets.some((productSet) => (
    NEW_UNIFIED_SKUS.includes(productSet.primaryProductId
      || productSet.addOnProductIds.some((addOnProductId) => NEW_UNIFIED_SKUS.includes(addOnProductId)))));

  // Check if Property Data product is selected
  const isPropertyDataSelected = checkIsPropertyDataSelected(selectedPrimaryProduct);

  if ((bluePrintId !== '')) {
    return (
      <EVPaper classes={{ root: clsx(classes.propertyDetails, 'blueprint') }} square={isMobile} onClick={handleRemoveFocus}>
        <EVGrid className={classes.propertyDetailsList}>
          {propertyTypeDetails()}
          <EVGrid style={{ padding: '0 8px' }}>
            <Typography variant="label-600" fontColor="charcoal-800" className={classes.propertyTypeHeading}>
              {t('finalize_order__blueprint_filename')}
            </Typography>
            <Typography variant="subtitle-400" data-testid="blue-print">{bluePrintFileName}</Typography>
          </EVGrid>
        </EVGrid>
      </EVPaper>
    );
  }

  return (
    <EVPaper classes={{ root: clsx(classes.propertyDetails, 'blueprint') }} square={isMobile} onClick={handleRemoveFocus}>
      <EVGrid className={classes.mapContainer} onClick={(e) => e.stopPropagation()}>
        {(mapPoints.length > 0) && (
        <Map
          useEmbeddedExplorer={isEntitledForEmbeddedExplorer && useEE}
          mapPoints={mapPoints}
          isComplex={isComplex}
          propertyType={propertyType}
          focusedPinIndex={focusedPointIndex}
          onFocusChange={handleFocusChange}
          mapOptions={{ showMapTypeSelector: false }}
        />
        )}
      </EVGrid>
      <EVGrid className={classes.propertyDetailsList}>
        {upgradeReportId && <UpgradeReport />}
        {!isNewSKUSelected && propertyTypeDetails()}
        {mapPoints.map((mapPoint, index, arr) => {
          const address = mapPoint?.address || {};
          const building = mapPoint?.building;
          return (
            <BuildingDetailsListItem
              key={`${building}-${index + 1}-${arr.length}`}
              address={address}
              building={building}
              isFocused={index === focusedPointIndex}
              isComplex={isComplex}
              index={index}
              handleClick={handleBuildingDetailClick(index)}
            />
          );
        })}
        {!bluePrintFileId && !isPropertyDataSelected && changesInLast4years()}
      </EVGrid>
    </EVPaper>
  );
}

PropertyDetails.defaultProps = {
  upgradeReportId: false,
};

PropertyDetails.propTypes = {
  upgradeReportId: PropTypes.bool,
};
export default PropertyDetails;
